/**
 * A prompt for a client whose e-mail address has not been confirmed.
 *
 * Until now a client only learned this when a withdrawal was refused. The banner
 * says it ahead of time and offers the one step that fixes it: mail the link
 * again to the address on file (the address is never typed here; the server
 * always uses the one on the account).
 *
 * Silent whenever the state cannot be read. A banner that claims "unconfirmed"
 * on a guess is worse than no banner, and the withdrawal screen still carries
 * the same prompt at the moment it matters.
 */
import { useEffect, useState } from 'react';
import emailVerificationService from '../../services/emailVerification.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';

/** "jane@example.com" becomes "j***@example.com"; anything unexpected becomes "". */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return '';
  return `${email.slice(0, 1)}***${email.slice(at)}`;
}

export const SENT_MESSAGE = 'Check your inbox. The link expires soon.';
export const SEND_FAILED_MESSAGE = 'We could not send the link. Please try again shortly.';

export interface EmailVerificationBannerViewProps {
  readonly email: string;
  readonly sending: boolean;
  /** Set once the link was requested; replaces the button. */
  readonly notice: string | null;
  /** Set when the request was refused, with the server's reason. */
  readonly error: string | null;
  readonly onSend: () => void;
}

export function EmailVerificationBannerView({
  email,
  sending,
  notice,
  error,
  onSend,
}: EmailVerificationBannerViewProps): JSX.Element {
  const masked = maskEmail(email);
  return (
    <aside
      role="status"
      aria-live="polite"
      className="rounded-lg p-3 mb-4 flex items-start justify-between gap-4 flex-wrap"
      style={{ background: colors.warningBg, border: '1px solid #FCD34D' }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold mb-1" style={{ color: colors.warning }}>
          Confirm your e-mail address
        </p>
        <p className="text-xs" style={{ color: colors.ink }}>
          {/* Only what is always true: whether a withdrawal waits on it is not known here. */}
          {masked ? `We can send a confirmation link to ${masked}.` : 'We can send you a confirmation link.'}
        </p>
        {notice && (
          <p className="text-xs mt-2" style={{ color: colors.ink }}>
            {notice}
          </p>
        )}
        {error && (
          <p className="text-xs mt-2" role="alert" style={{ color: colors.danger }}>
            {error}
          </p>
        )}
      </div>
      {!notice && (
        <button
          type="button"
          onClick={onSend}
          disabled={sending}
          className="text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap"
          style={{
            background: sending ? colors.disabledBg : colors.navy,
            color: sending ? colors.disabledText : '#FFFFFF',
            cursor: sending ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? 'Sending the link…' : 'Send me the link'}
        </button>
      )}
    </aside>
  );
}

export default function EmailVerificationBanner(): JSX.Element | null {
  const [email, setEmail] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    emailVerificationService
      .getStatus()
      .then((status) => {
        if (live && !status.verified) setEmail(status.email);
      })
      // Unreadable state: say nothing rather than a wrong claim.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  if (email === null) return null;

  const send = (): void => {
    setSending(true);
    setError(null);
    emailVerificationService
      .requestLink()
      .then(() => setNotice(SENT_MESSAGE))
      .catch((err: unknown) => setError(getApiErrorMessage(err, SEND_FAILED_MESSAGE)))
      .finally(() => setSending(false));
  };

  return (
    <EmailVerificationBannerView
      email={email}
      sending={sending}
      notice={notice}
      error={error}
      onSend={send}
    />
  );
}
